import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { Check, ExternalLink, Link2, MapPin } from "lucide-react";
import { useStationManagementApi } from "../../hooks/useStationManagement";
import { googleMapsSearchUrl, isShortMapsLink, parseCoordinates } from "./mapsLink";
import { Button, FormField, TextField, inputClass } from "./ui";

// Latitude/longitude for a location, three ways:
//  1. paste a Google Maps link (full or maps.app.goo.gl short link) or "lat, lng"
//  2. open Google Maps at the typed address, pick the spot, paste its link back
//  3. type the numbers
// A small map preview confirms the pin once both values are set.
export default function CoordinatePicker({ form, errors, onChange, address }) {
  const api = useStationManagementApi();
  const [link, setLink] = useState("");
  const [linkError, setLinkError] = useState("");
  const [resolving, setResolving] = useState(false);
  const [applied, setApplied] = useState(false);

  const lat = form.latitude === "" ? NaN : Number(form.latitude);
  const lng = form.longitude === "" ? NaN : Number(form.longitude);
  const hasPin = Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

  const apply = async (value = link) => {
    const text = value.trim();
    setApplied(false);
    setLinkError("");
    if (!text) return;

    let source = text;
    if (isShortMapsLink(text)) {
      setResolving(true);
      try {
        source = (await api.resolveMapsLink(text)).url;
      } catch (err) {
        setLinkError(err.message || "Couldn't open that link. Open it in Google Maps and copy the full URL instead.");
        return;
      } finally {
        setResolving(false);
      }
    }

    const coords = parseCoordinates(source);
    if (!coords) {
      setLinkError(
        isShortMapsLink(text) || /google\.|goo\.gl/i.test(text)
          ? "This link has no coordinates (e.g. a business name only). In Google Maps, drop a pin on the exact spot and share that instead."
          : "Paste a Google Maps link or coordinates like 17.385044, 78.486671"
      );
      return;
    }
    onChange("latitude", String(coords.lat));
    onChange("longitude", String(coords.lng));
    setApplied(true);
    toast.success(`Pin set to ${coords.lat}, ${coords.lng}`);
  };

  return (
    <div className="space-y-3 rounded-xl border border-neutral-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-13 font-medium text-neutral-900">
            <MapPin className="h-4 w-4" /> Map position
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">Paste a Google Maps link, or pick the spot on Google Maps.</p>
        </div>
        <Button
          size="sm"
          icon={ExternalLink}
          onClick={() => window.open(googleMapsSearchUrl({ lat: hasPin ? lat : "", lng: hasPin ? lng : "", address }), "_blank", "noopener,noreferrer")}
        >
          Pick on Google Maps
        </Button>
      </div>

      <FormField
        error={linkError}
        hint={
          applied
            ? undefined
            : "In Google Maps: drop a pin on the exact spot → Share → Copy link, then paste it here. On a computer you can also right-click the spot and click the coordinates to copy them."
        }
      >
        <div className="flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={link}
              onChange={(e) => {
                setLink(e.target.value);
                setLinkError("");
                setApplied(false);
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData("text");
                if (pasted) {
                  e.preventDefault();
                  setLink(pasted);
                  apply(pasted);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  apply();
                }
              }}
              placeholder="https://maps.app.goo.gl/…  or  17.385044, 78.486671"
              className={`${inputClass(linkError)} pl-9`}
              aria-label="Google Maps link or coordinates"
            />
          </div>
          <Button onClick={() => apply()} loading={resolving} disabled={!link.trim()} icon={applied ? Check : undefined}>
            {applied ? "Applied" : "Use link"}
          </Button>
        </div>
      </FormField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField label="Latitude" name="latitude" type="number" step="0.000001" form={form} errors={errors} onChange={onChange} placeholder="17.385044" />
        <TextField label="Longitude" name="longitude" type="number" step="0.000001" form={form} errors={errors} onChange={onChange} placeholder="78.486671" />
      </div>

      {hasPin && (
        <div className="overflow-hidden rounded-lg border border-neutral-200">
          <iframe
            title="Location preview"
            src={`https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`}
            className="h-48 w-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="flex items-center justify-between gap-2 border-t border-neutral-100 bg-neutral-50 px-3 py-1.5 text-xs text-neutral-500">
            <span className="tabular-nums">{lat}, {lng}</span>
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-neutral-800 hover:underline"
            >
              Open in Google Maps <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
